// The chat unread total on the installed app's icon (where the Badging API
// exists), and read positions passed to the service worker, which closes
// notifications for messages read here or on another device.
import { useEffect, useRef, useState } from "react";
import { session } from "../api";
import { appBadge, newReads, type Conversation } from "./chat";

function setBadge(count: number | null) {
  if (!("setAppBadge" in navigator)) return;
  void (count === null ? navigator.clearAppBadge() : navigator.setAppBadge(count)).catch(() => undefined);
}

export function useChatNotifications(conversations: readonly Conversation[] | null, total: number) {
  const badge = conversations === null ? null : appBadge(total);
  const loaded = conversations !== null;
  useEffect(() => {
    if (loaded) setBadge(badge);
  }, [loaded, badge]);
  // Signing out (here, in another tab or by revocation) leaves no count or read
  // positions behind for the next account; a public page in between keeps them.
  useEffect(
    () => () => {
      if (session.current()) return;
      setBadge(null);
      void navigator.serviceWorker
        ?.getRegistration()
        .then((registration) => registration?.active?.postMessage({ type: "chat_forget" }))
        .catch(() => undefined);
    },
    [],
  );

  // Read positions only change on reads, so this runs rarely.
  const posted = useRef(new Map<string, number>());
  // A new or updated worker (alerts just enabled, a new sw.js) is told every read position again.
  const [worker, setWorker] = useState(0);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const changed = () => {
      posted.current = new Map();
      setWorker((n) => n + 1);
    };
    navigator.serviceWorker.addEventListener("controllerchange", changed);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", changed);
  }, []);
  const readKey = conversations?.map((c) => `${c.id}:${c.read_seq}`).join(",") ?? "";
  useEffect(() => {
    if (!conversations || !("serviceWorker" in navigator)) return;
    const reads = newReads(posted.current, conversations);
    if (reads.length === 0) return;
    void navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        const worker = registration?.active;
        if (!worker) return;
        worker.postMessage({ type: "chat_read", reads });
        for (const read of reads) posted.current.set(read.conversation_id, read.seq);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readKey, worker]);
}
